import { useState } from 'react';
import { Filter, SearchInput, SelectInput, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Avatar, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Assignment } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';
import { TES_ASSIGNMENT_STATUS, STATUS_COLORS, DEEMING_RISK_COLORS } from './constants';
import { DeemingCountdownBar } from './DeemingCountdownBar';

interface AssignmentItem {
  id: string;
  status: string;
  employee?: { id: string };
  vendorName?: string;
  role?: string;
  startDate: string;
  deemingRisk?: string;
}

export const TESAssignmentList = () => {
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [now] = useState(() => Date.now());
  const { data: assignments, isLoading } = useGetList('tes-assignments', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const stats = {
    total: assignments?.length ?? 0,
    active: assignments?.filter((a: AssignmentItem) => a.status === 'Active').length ?? 0,
    pending: assignments?.filter((a: AssignmentItem) => a.status === 'Pending').length ?? 0,
    suspended: assignments?.filter((a: AssignmentItem) => a.status === 'Suspended').length ?? 0,
    completed: assignments?.filter((a: AssignmentItem) => a.status === 'Completed').length ?? 0,
    terminated: assignments?.filter((a: AssignmentItem) => a.status === 'Terminated').length ?? 0,
    highRisk: assignments?.filter((a: AssignmentItem) => a.deemingRisk === 'High' || a.deemingRisk === 'Critical').length ?? 0,
  };

  const statusCounts: Record<string, number> = assignments?.reduce((acc: Record<string, number>, a: AssignmentItem) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(statusCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="TES Assignments"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'High Risk', value: String(stats.highRisk), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {assignments?.map((assignment: AssignmentItem) => (
            <Paper key={assignment.id} sx={{ p: 2 }} onClick={() => navigate(`/tes-assignments/${assignment.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: STATUS_COLORS[assignment.status] === 'success' ? 'success.main' : STATUS_COLORS[assignment.status] === 'warning' ? 'warning.main' : 'grey.500', fontSize: 14, color: 'white' }}>
                    {getEmployeeName(employeeMap, assignment.employee?.id)?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, assignment.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{assignment.vendorName}</Typography>
                  </Box>
                </Box>
                <Chip label={assignment.status} size="small" color={STATUS_COLORS[assignment.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{assignment.role}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{assignment.startDate}</Typography>
              </Box>
              <DeemingCountdownBar startDate={assignment.startDate} thresholdDays={90} />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="TES Assignments"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'info.main' },
          { label: 'High Risk', value: String(stats.highRisk), color: 'error.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tenure</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Deeming Risk</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments?.map((assignment: AssignmentItem) => {
              const tenureDays = Math.floor((now - new Date(assignment.startDate).getTime()) / (1000 * 60 * 60 * 24));
              return (
                <TableRow
                  key={assignment.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tes-assignments/${assignment.id}/show`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'warning.main', fontSize: 12, color: 'white' }}>
                        {getEmployeeName(employeeMap, assignment.employee?.id)?.charAt(0) || '?'}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, assignment.employee?.id)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{assignment.vendorName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{assignment.role}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{assignment.startDate}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{tenureDays}d</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={assignment.deemingRisk || 'Low'} size="small" sx={{ bgcolor: DEEMING_RISK_COLORS[assignment.deemingRisk || 'Low'], color: 'white' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={assignment.status} size="small" color={STATUS_COLORS[assignment.status] as ChipColor} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={assignments?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!assignments || assignments.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No assignments found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const TESAssignmentFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search employee or vendor" />
    <SelectInput source="status" choices={TES_ASSIGNMENT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="deemingRisk" choices={['Low', 'Medium', 'High', 'Critical'].map(r => ({ id: r, name: r }))} />
  </Filter>
);
